import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Watch, MoonStars } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import InfoTip from "@/components/InfoTip";

const SOURCE_NAMES = { apple: "Apple Health", oura: "Oura", whoop: "Whoop", googlehealth: "Google Health" };

const SLIDERS = [
  { key: "sleep_quality", label: "Sleep quality", min: 1, max: 10 },
  { key: "energy", label: "Energy", min: 1, max: 10 },
  { key: "mood", label: "Mood", min: 1, max: 10 },
  { key: "motivation", label: "Motivation", min: 1, max: 10 },
  { key: "stress", label: "Stress", min: 1, max: 10 },
  { key: "soreness", label: "Soreness", min: 1, max: 10 },
  { key: "nutrition_quality", label: "Nutrition yesterday", min: 1, max: 10 },
];

const LOCATIONS = ["home", "gym", "office", "outdoor", "hotel"];

export default function CheckinDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({
    sleep_hours: 7, sleep_quality: 7, energy: 7, mood: 7, motivation: 7,
    stress: 4, soreness: 3, nutrition_quality: 7, minutes_available: 40,
    location: "home", steps: 6000, hrv: 55, resting_hr: 60,
  });
  const [busy, setBusy] = useState(false);
  const [synced, setSynced] = useState(false);
  const [autoSource, setAutoSource] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    api.get("/wearables/sync").then(({ data }) => {
      if (data.connected && data.data && Object.keys(data.data).length) {
        setForm((f) => ({ ...f, ...data.data }));
        setAutoSource(data.sources.map((s) => SOURCE_NAMES[s] || s).join(" + "));
        setSynced(true);
      } else {
        setAutoSource(null);
        setSynced(false);
      }
    }).catch(() => {});
  }, [open]);

  const sync = async () => {
    try {
      const { data } = await api.post("/sync-wearable");
      setForm((f) => ({ ...f, sleep_hours: data.sleep_hours, sleep_quality: data.sleep_quality, hrv: data.hrv, resting_hr: data.resting_hr, steps: data.steps }));
      setSynced(true);
      toast.success(`Synced from ${data.source}`);
    } catch { toast.error("Sync failed"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/checkin", form);
      toast.success("Ikavio is planning your optimal day…");
      onOpenChange(false);
      onDone?.();
    } catch { toast.error("Check-in failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[88vh] overflow-y-auto scroll-thin" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl tracking-tight">How are you today?</DialogTitle>
          <DialogDescription>A 30-second check-in so Ikavio can adapt your optimal day.</DialogDescription>
        </DialogHeader>

        {autoSource ? (
          <div
            data-testid="wearable-autofill-banner"
            className="w-full flex items-center justify-center gap-2 bg-brand/10 text-brand rounded-2xl py-3 text-sm font-medium"
          >
            <Watch size={18} weight="fill" />
            Auto-filled from {autoSource} ✓
          </div>
        ) : (
          <button
            data-testid="sync-wearable-btn"
            onClick={sync}
            className="w-full flex items-center justify-center gap-2 border border-border rounded-2xl py-3 text-sm font-medium hover:bg-secondary transition-colors"
          >
            <Watch size={18} weight="fill" color="#8B7CF6" />
            {synced ? "Wearable synced ✓ — re-sync" : "Sync simulated wearable"}
          </button>
        )}

        <div className="grid grid-cols-3 gap-3 mt-1">
          <NumField label="Sleep (hrs)" value={form.sleep_hours} step={0.5} onChange={(v) => set("sleep_hours", v)} />
          <NumField label="HRV (ms)" tip="hrv" value={form.hrv} onChange={(v) => set("hrv", v)} />
          <NumField label="Rest HR" tip="resting-hr" value={form.resting_hr} onChange={(v) => set("resting_hr", v)} />
        </div>

        <div className="space-y-3.5 mt-1">
          {SLIDERS.map((s) => (
            <div key={s.key}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">{s.label}</span>
                <span className="text-brand font-semibold tabular-nums">{form[s.key]}</span>
              </div>
              <input
                data-testid={`checkin-${s.key}`}
                type="range" min={s.min} max={s.max} value={form[s.key]}
                onChange={(e) => set(s.key, Number(e.target.value))}
                className="w-full accent-brand"
              />
            </div>
          ))}
        </div>

        <div>
          <div className="label-eyebrow mb-2">Time available (min)</div>
          <input data-testid="checkin-minutes" type="range" min={10} max={120} step={5} value={form.minutes_available}
            onChange={(e) => set("minutes_available", Number(e.target.value))} className="w-full accent-brand" />
          <div className="text-sm text-brand font-semibold">{form.minutes_available} min</div>
        </div>

        <div>
          <div className="label-eyebrow mb-2">Where are you?</div>
          <div className="flex flex-wrap gap-2">
            {LOCATIONS.map((l) => (
              <button key={l} data-testid={`location-${l}`} onClick={() => set("location", l)}
                className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${form.location === l ? "bg-ink text-bg" : "bg-secondary hover:bg-border"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <button
          data-testid="checkin-submit-btn"
          onClick={submit}
          disabled={busy}
          className="w-full bg-brand text-white rounded-full py-3.5 font-medium hover:bg-ink transition-colors disabled:opacity-60 mt-2 flex items-center justify-center gap-2"
        >
          <MoonStars size={18} weight="fill" />
          {busy ? "Planning your day…" : "Generate my optimal day"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function NumField({ label, value, onChange, step = 1, tip }) {
  return (
    <div>
      <div className="label-eyebrow mb-1.5 flex items-center gap-1">{label}{tip && <InfoTip termId={tip} />}</div>
      <input
        type="number" step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-brand"
      />
    </div>
  );
}
